import { useState } from "react";
import { Link } from "react-router-dom";
import { useHero } from "@/hooks/useHero";
import { heroContent } from "@/data/heroContent";
import NoticiasFilters from "./components/NoticiasFilters";
import NoticiaDestacadaCard from "./components/NoticiaDestacadaCard";
import NoticiaPequenaCard from "./components/NoticiaPequenaCard";
import NoticiaListItem from "./components/NoticiaListItem";
import FacebookFeed from "./components/FacebookFeed";
import InstagramFeed from "./components/InstagramFeed";
import TwitterFeed from "./components/TwitterFeed";
import LinkedInFeed from "./components/LinkedInFeed";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { useEffect } from "react";

const heroFallback = heroContent.find((h) => h.page === "noticias")!;

interface NoticiaDB {
  id: number;
  titulo: string;
  subtitulo?: string;
  extracto?: string;
  imagen?: string;
  fecha?: string;
  comuna?: string;
  categoria?: string;
  segmento?: string;
  created_at?: string;
}

const socials = [
  { icon: "ri-twitter-x-fill", label: "X", href: "https://x.com" },
  { icon: "ri-youtube-fill", label: "YouTube", href: "https://youtube.com" },
  { icon: "ri-instagram-fill", label: "Instagram", href: "https://www.instagram.com/masmed_tarapaca" },
  { icon: "ri-facebook-fill", label: "Facebook", href: "https://www.facebook.com/profile.php?id=61586569725415" },
  { icon: "ri-linkedin-fill", label: "LinkedIn", href: "https://linkedin.com" },
];

export default function Noticias() {
  const [searchQuery, setSearchQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const { data: noticias } = useSupabaseData<NoticiaDB>("noticias", []);
  const { data: noticiasHero } = useHero("noticias", heroFallback);

  // Update page title from hero seo_title
  useEffect(() => {
    const seoTitle = noticiasHero?.seo_title ?? heroFallback.seoTitle ?? "Noticias | MásMed Tarapacá";
    if (seoTitle) {
      document.title = seoTitle;
    }
  }, [noticiasHero]);

  // Noticia destacada (primera)
  const noticiaDestacada = noticias[0];
  // 4 noticias pequeñas
  const noticiasPequenas = noticias.slice(1, 5);
  // Listado inferior (6 items)
  const noticiasListado = noticias.slice(0, 6);

  return (
    <div>
      {/* Hero Noticias */}
      <section className="relative w-full h-[580px] sm:h-[640px] md:h-[720px] lg:h-[760px] flex flex-col overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${noticiasHero?.background_image ?? heroFallback.backgroundImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-700/60 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-cyan-600/25 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-l from-orange-500/30 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-black/10" />

        <div className="relative z-10 flex-1 flex flex-col items-center justify-between w-full px-4 md:px-8 lg:px-12 pt-24 md:pt-28 pb-10 md:pb-12">
          {/* Social icons - left vertical column (hidden on small mobile) */}
          <div className="hidden md:flex absolute left-6 lg:left-10 top-1/2 -translate-y-1/2 flex-col gap-3 lg:gap-4 z-20">
            {socials.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="w-10 h-10 lg:w-11 lg:h-11 rounded-full border-2 border-white/80 bg-white/10 hover:bg-white/25 hover:border-white hover:scale-110 flex items-center justify-center text-white text-base lg:text-lg transition-all duration-300 cursor-pointer backdrop-blur-sm"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={social.icon}></i>
                </div>
              </a>
            ))}
          </div>

          <div className="flex-1 flex flex-col items-center justify-center w-full">
            <div className="text-center max-w-3xl">
              <h1 className="font-utendo text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight drop-shadow-lg">
                Noticias
              </h1>
              <p className="text-white/90 text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed max-w-2xl mx-auto mt-4 md:mt-5 drop-shadow"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>
                Conoce los avances, actividades y presencia de MásMed en la región.
              </p>

              {/* Social icons - mobile row */}
              <div className="flex md:hidden flex-row justify-center gap-3 mt-4">
                {socials.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-9 h-9 rounded-full border-2 border-white/80 bg-white/10 hover:bg-white/25 hover:border-white flex items-center justify-center text-white text-sm transition-all duration-300 cursor-pointer backdrop-blur-sm"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={social.icon}></i>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full max-w-4xl mx-auto text-center">
            <p className="text-white text-xs sm:text-sm md:text-base leading-relaxed drop-shadow-md max-w-3xl mx-auto"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
              Proyecto financiado por el Gobierno Regional de Tarapacá y su Consejo Regional a través del Fondo Regional para la Productividad y Desarrollo (FRPD).
            </p>
            <div className="mt-3 md:mt-4 flex flex-wrap items-center justify-center gap-3 md:gap-5 lg:gap-8">
              <span className="text-white text-xs md:text-sm uppercase tracking-wider drop-shadow-md"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>Financia:</span>
              <img
                src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/6576e2ace5ad881c20d8c13d63df08f0.png"
                alt="Gobierno Regional de Tarapacá"
                className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto object-contain drop-shadow-md"
                loading="lazy"
              />
              <img
                src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/cea5546c3b2b72c7ff9e242a7eae203e.png"
                alt="CORE Tarapacá"
                className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto object-contain drop-shadow-md"
                loading="lazy"
              />
              <span className="text-white text-xs md:text-sm uppercase tracking-wider drop-shadow-md"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>Ejecuta:</span>
              <img
                src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/200e423cba0b1ea675826f32c4682d50.png"
                alt="UNAP Universidad Arturo Prat"
                className="h-8 sm:h-9 md:h-10 lg:h-12 w-auto object-contain drop-shadow-md"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Buscador */}
      <section className="pt-8 md:pt-10 pb-2 md:pb-4 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-full shadow-lg px-5 md:px-6 h-[48px] md:h-[54px] flex items-center gap-3 border border-masmed-gray-100">
              <input
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 text-sm md:text-base text-masmed-gray-900 placeholder-masmed-gray-400 focus:outline-none bg-transparent"
              />
              <i className="ri-search-line text-masmed-gray-500 text-xl flex-shrink-0" />
            </div>
          </div>
        </div>
      </section>

      {/* Últimas Noticias */}
      <section className="py-4 md:py-6 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-12">
          {/* Filtros arriba alineados a la izquierda */}
          <div className="mb-6">
            <NoticiasFilters />
          </div>

          {/* Layout principal: destacada izquierda + título + 4 pequeñas derecha */}
          <div className="flex flex-col lg:flex-row gap-5 items-stretch">
            {/* Noticia destacada grande izquierda */}
            <div className="w-full lg:w-1/2 flex">
              {noticiaDestacada && <NoticiaDestacadaCard noticia={noticiaDestacada} />}
            </div>

            {/* Derecha: título + 4 noticias pequeñas */}
            <div className="w-full lg:w-1/2 flex flex-col">
              {/* Título centrado sobre las 4 cards pequeñas */}
              <h2 className="text-center text-masmed-gray-800 text-lg md:text-xl mb-3 md:mb-4">
                Últimas noticias
              </h2>
              <div className="grid grid-cols-2 gap-3 md:gap-4 flex-1">
                {noticiasPequenas.map((noticia) => (
                  <NoticiaPequenaCard key={noticia.id} noticia={noticia} />
                ))}
              </div>
            </div>
          </div>

          {/* Listado inferior dos columnas, separado */}
          <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-6 md:gap-x-10 lg:gap-x-16">
            <div>
              {noticiasListado.slice(0, 3).map((noticia) => (
                <NoticiaListItem key={noticia.id} noticia={noticia} />
              ))}
            </div>
            <div>
              {noticiasListado.slice(3, 6).map((noticia) => (
                <NoticiaListItem key={noticia.id} noticia={noticia} />
              ))}
            </div>
          </div>

          {/* Botón Ver todas */}
          <div className="mt-8 md:mt-10 flex justify-center">
            <button
              onClick={() => setShowAll(!showAll)}
              type="button"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white text-sm md:text-base rounded-md hover:bg-primary-dark transition-colors duration-200 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
            >
              {showAll ? "Ver menos" : "Ver todas"}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line transition-transform duration-300 ${showAll ? "rotate-180" : ""}`}></i>
              </span>
            </button>
          </div>

          {/* Desplegable vertical con todas las noticias */}
          <div
            className={`overflow-hidden transition-all duration-500 ease-in-out ${
              showAll ? "max-h-[2000px] opacity-100 mt-8 md:mt-10" : "max-h-0 opacity-0"
            }`}
          >
            <div className="border-t border-masmed-gray-200 pt-8 md:pt-10">
              <h3 className="text-masmed-gray-800 text-lg md:text-xl font-bold mb-5 md:mb-6"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>
                Todas las noticias
              </h3>
              <div className="flex flex-col gap-0">
                {noticias.map((noticia) => (
                  <NoticiaListItem key={noticia.id} noticia={noticia} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Últimas Publicaciones */}
      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-[1080px] mx-auto px-4 md:px-8 lg:px-12">
          {/* Título centrado */}
          <h2 className="text-center text-masmed-gray-700 text-xl md:text-2xl mb-8 md:mb-10">
            Últimas publicaciones
          </h2>

          {/* Facebook - bloque completo con Page Plugin */}
          <div className="mb-6 md:mb-8">
            <FacebookFeed />
          </div>

          {/* Instagram - bloque completo con grid y CTA */}
          <div className="mb-6 md:mb-8">
            <InstagramFeed />
          </div>

          {/* X + LinkedIn - grilla 2 columnas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-6 md:mb-8">
            <TwitterFeed />
            <LinkedInFeed />
          </div>
        </div>
      </section>
    </div>
  );
}